import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  ChatPreview as ChatPreviewGRPC,
  Chat as ChatGRPC,
} from '@trustero/trustero-api-web/lib/chat/chat_pb'
import log from 'loglevel'
import { FlexColumn } from 'src/components/Reusable/Flex'
import {
  ModalFormId,
  ModalForm,
  useConfirmationModal,
} from 'src/components/ModalForms'
import { FlexAlign } from 'src/components/Reusable/Flex'
import {
  useHideModal,
  useIsShowModal,
  useModalState,
} from 'src/Modal/ModalStateContext'
import { Spinner } from 'src/Throbber/Spinner'
import { useCopyToClipboard } from 'usehooks-ts'
import { Dropdown } from 'react-bootstrap'
import formatDate from 'src/Utils/formatDate'
import { capitalizeFirstLetter } from 'src/Utils/globalHelpers'
import { useUser } from 'src/components/async/Users'
import { DerivedTextInput } from 'src/components/Reusable/Inputs/TextInput/TextInput'
import { GridRowMenu } from 'src/components/Reusable/Grid/GridRowMenu'
import { PrivateSVG, PublicSVG } from 'src/components/Icons/Basic'
import { getEditChatNameError } from '../chatpanel/chatPanel.components'
import { useChatPermalink, useUpdateChat } from '../trusteroIntelligence.hooks'
import { useChatState } from '../trusteroIntelligence.context'
import {
  ChatHistoryItemContainer,
  ChatHistoryItemTitle,
  ChatHistoryListContainer,
  ChatHistoryGroupDate,
  ChatHistoryGroupContainer,
  ChatHistoryEmptyStateContainer,
  ChatHistoryFilterToggle,
  ChatHistoryFilterItem,
  ChatHistoryFilterDropdown,
} from './chatHistoryPanel.styles'
import {
  ChatNameProvider,
  useChatNameContext,
} from './chatHistoryPanel.context'

export type ChatHistoryListProps = {
  activeChatId?: string
  chats?: ChatPreviewGRPC[]
  deleteChat?: (chatId: string) => void
  isLoading: boolean
  navigateToChat: (chatId: string) => void
  reloadChats: () => void
}

type ChatPreview = {
  id: string
  createdAt: number
  updatedAt: number
  name: string
  ownerId?: string
  isPrivate: boolean
}

export const ChatHistoryList = ({
  activeChatId,
  chats,
  deleteChat,
  isLoading,
  navigateToChat,
  reloadChats,
}: ChatHistoryListProps): JSX.Element => {
  const [currentChatId, setCurrentChatId] = useState<string | undefined>(
    undefined,
  )
  const chatsByDate = useMemo(() => {
    const result: Record<string, ChatPreview[]> = {}
    const chatsGroupedByDate: Record<string, ChatPreview[]> =
      chats?.reduce(chatPreviewReducer, result) ?? result

    return Object.entries(chatsGroupedByDate).sort(
      (a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime(),
    )
  }, [chats])

  if (isLoading) {
    return (
      <FlexColumn justify={FlexAlign.CENTER} align={FlexAlign.CENTER}>
        <Spinner color="secondary" size="s" />
      </FlexColumn>
    )
  }

  if (!Object.keys(chatsByDate).length) {
    return (
      <ChatHistoryEmptyStateContainer>
        No chats created yet, ask <i>Trustero Intelligence</i> anything to start
        a chat!
      </ChatHistoryEmptyStateContainer>
    )
  }

  return (
    <ChatNameProvider>
      <ChatHistoryListContainer>
        {chatsByDate.map(([date, chatsGroup]) => (
          <ChatHistoryGroup
            key={date}
            date={date}
            chats={chatsGroup}
            activeChatId={activeChatId}
            deleteChat={deleteChat}
            navigateToChat={navigateToChat}
            reloadChats={reloadChats}
            setCurrentChatId={setCurrentChatId}
            currentChatId={currentChatId}
          />
        ))}
        <EditChatNameModal />
      </ChatHistoryListContainer>
    </ChatNameProvider>
  )
}

type ChatHistoryGroupProps = {
  date: string
  chats: ChatPreview[]
  activeChatId?: string
  deleteChat?: (chatId: string) => void
  navigateToChat: (chatId: string) => void
  reloadChats: () => void
  setCurrentChatId: (chatId: string) => void
  currentChatId: string | undefined
}

const ChatHistoryGroup = ({
  date,
  chats,
  activeChatId,
  deleteChat,
  navigateToChat,
  reloadChats,
  setCurrentChatId,
  currentChatId,
}: ChatHistoryGroupProps): JSX.Element => {
  const sortedChats = useMemo(() => {
    return chats.sort((a, b) => b.updatedAt - a.updatedAt)
  }, [chats])

  return (
    <ChatHistoryGroupContainer>
      <ChatHistoryGroupDate>{date}</ChatHistoryGroupDate>
      {sortedChats.map((chat) => (
        <ChatHistoryItem
          key={chat.id}
          chat={chat}
          isSelected={activeChatId === chat.id}
          deleteChat={deleteChat}
          navigateToChat={navigateToChat}
          reloadChats={reloadChats}
          setCurrentChatId={setCurrentChatId}
          currentChatId={currentChatId}
        />
      ))}
    </ChatHistoryGroupContainer>
  )
}

type ChatHistoryItemProps = {
  chat: ChatPreview
  isSelected?: boolean
  deleteChat?: (chatId: string) => void
  navigateToChat: (chatId: string) => void
  reloadChats: () => void
  setCurrentChatId: (chatId: string) => void
  currentChatId: string | undefined
}

const ChatHistoryItem = ({
  chat,
  isSelected,
  deleteChat,
  navigateToChat,
  reloadChats,
  setCurrentChatId,
  currentChatId,
}: ChatHistoryItemProps): JSX.Element => {
  const [isHovered, setIsHovered] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const updateChat = useUpdateChat()
  const { setChatTitle } = useChatState(chat.id)

  const handleMouseHover = useCallback(() => {
    setIsHovered((prev) => !prev || isOpen)
  }, [isOpen])

  const handleChatClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.metaKey || e.ctrlKey) {
        return
      }
      navigateToChat(chat.id)
    },
    [chat, navigateToChat],
  )

  const handleLinkClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (e.metaKey || e.ctrlKey) {
        return
      }
      e.preventDefault()
      e.stopPropagation()
    },
    [],
  )

  const chatPermalink = useChatPermalink(chat.id)

  const onUpdateChatTitle = useCallback(
    async (chatName: string) => {
      const chatData = new ChatGRPC()
      chatData.setId(chat.id)
      chatData.setName(chatName)
      chatData.setIsPrivate(chat.isPrivate)
      await updateChat(chatData)
      reloadChats()
      setChatTitle(chatName)
    },
    [chat.id, chat.isPrivate, updateChat, reloadChats, setChatTitle],
  )

  const onTogglePrivate = useCallback(async () => {
    const chatData = new ChatGRPC()
    chatData.setId(chat.id)
    chatData.setName(chat.name)
    chatData.setIsPrivate(!chat.isPrivate)
    await updateChat(chatData)
    reloadChats()
  }, [chat.id, chat.name, chat.isPrivate, updateChat, reloadChats])

  const onToggle = useCallback(
    (open: boolean) => {
      setIsOpen(open)
      setCurrentChatId(open ? chat.id : '')
    },
    [chat.id, setCurrentChatId],
  )

  useEffect(() => {
    if (currentChatId !== chat.id && !isSelected) {
      setIsHovered(false)
    }
  }, [chat.id, currentChatId, isSelected])

  return (
    <ChatHistoryItemContainer
      href={chatPermalink}
      onMouseEnter={handleMouseHover}
      onMouseLeave={handleMouseHover}
      $isHighlighted={isOpen || isSelected}
      onClick={handleLinkClick}
    >
      <ChatHistoryItemTitle onClick={handleChatClick}>
        {chat.name}
      </ChatHistoryItemTitle>
      {(isHovered || isSelected || (isOpen && currentChatId === chat.id)) && (
        <ChatHistoryItemActions
          chatName={chat.name}
          chatId={chat.id}
          chatOwnerId={chat.ownerId}
          isPrivate={chat.isPrivate}
          onTogglePrivate={onTogglePrivate}
          deleteChat={deleteChat}
          onUpdateChatTitle={onUpdateChatTitle}
          onToggle={onToggle}
          currentChatId={currentChatId}
        />
      )}
    </ChatHistoryItemContainer>
  )
}

type ChatHistoryItemActionsProps = {
  chatName: string
  chatId: string
  chatOwnerId?: string
  isPrivate: boolean
  onTogglePrivate: () => void
  deleteChat?: (chatId: string) => void
  onUpdateChatTitle: (chatName: string) => void
  onToggle: (isOpen: boolean) => void
  currentChatId: string | undefined
}

const ChatHistoryItemActions = ({
  chatName,
  chatId,
  chatOwnerId,
  isPrivate,
  onTogglePrivate,
  deleteChat,
  onUpdateChatTitle,
  onToggle,
  currentChatId,
}: ChatHistoryItemActionsProps): JSX.Element => {
  const { setChatName, setUpdateChatName } = useChatNameContext()
  const { data: user } = useUser()
  const [_, copyToClipboard] = useCopyToClipboard()
  const permalink = useChatPermalink(chatId)
  const deleteChatFunc = useDeleteChatModal({
    chatName,
    chatId,
    deleteChat:
      deleteChat ??
      (() => {
        return
      }),
  })

  const copyPermalink = useCallback(() => {
    copyToClipboard(permalink)
  }, [permalink, copyToClipboard])

  const handleDeleteChat = useCallback(() => {
    log.info('Deleting chat', chatId)
    deleteChatFunc()
  }, [chatId, deleteChatFunc])

  const { openModal } = useModalState()

  const menuItems = useMemo(() => {
    if (currentChatId !== chatId) {
      return []
    }

    const items = [
      {
        key: 'copylink',
        item: <div onClick={copyPermalink}>Copy Link</div>,
      },
    ]

    if (chatOwnerId === user?.getId()) {
      items.push({
        key: 'rename',
        item: (
          <div
            onClick={() => {
              setChatName(chatName)
              setUpdateChatName(() => onUpdateChatTitle)
              openModal(ModalFormId.RENAME_CHAT)
            }}
            style={{ width: '100%' }}
          >
            Rename
          </div>
        ),
      })
      items.push({
        key: 'visibility',
        item: (
          <div
            onClick={onTogglePrivate}
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          >
            {isPrivate ? (
              <PrivateSVG width="14px" height="14px" />
            ) : (
              <PublicSVG width="14px" height="14px" />
            )}
            {isPrivate ? 'Private — make public' : 'Public — make private'}
          </div>
        ),
      })
    }

    if (deleteChat) {
      items.push({
        key: 'delete',
        item: (
          <div onClick={handleDeleteChat} style={{ width: '100%' }}>
            Delete
          </div>
        ),
      })
    }
    return items
  }, [
    currentChatId,
    chatId,
    chatName,
    onUpdateChatTitle,
    openModal,
    handleDeleteChat,
    chatOwnerId,
    user,
    copyPermalink,
    setChatName,
    setUpdateChatName,
    deleteChat,
    isPrivate,
    onTogglePrivate,
  ])

  return <GridRowMenu items={menuItems} id={chatId} onToggle={onToggle} />
}

export const useDeleteChatModal = ({
  chatName,
  chatId,
  deleteChat,
}: {
  chatName: string
  chatId: string
  deleteChat: (chatId: string) => void
}): (() => void) => {
  const onConfirmCB = useCallback(async () => {
    try {
      if (chatId) {
        await deleteChat(chatId)
      }
    } catch (err) {
      log.error('Error while deleting chat:', err)
    }
  }, [chatId, deleteChat])

  return useConfirmationModal({
    title: `Are you sure you want to delete the chat "${chatName}"?`,
    body: (
      <FlexColumn gap={10} align={FlexAlign.FLEX_START}>
        This cannot be undone.
      </FlexColumn>
    ),
    confirmText: 'Delete',
    onConfirmCB,
    throbber: {
      color: 'primary',
      size: 'xl',
    },
  })
}

export type ChatHistoryFilterType = 'all' | 'mine' | 'private'

const ChatHistoryFilterLabel: Record<ChatHistoryFilterType, string> = {
  all: 'All Chats',
  mine: 'My Chats',
  private: 'My Private Chats',
}

export type ChatHistoryFilterProps = {
  currentFilter: ChatHistoryFilterType
  setFilter: (filter: ChatHistoryFilterType) => void
}

export const ChatHistoryFilter = ({
  currentFilter,
  setFilter,
}: ChatHistoryFilterProps): JSX.Element => {
  const types = useMemo<ChatHistoryFilterType[]>(
    () => ['all', 'mine', 'private'],
    [],
  )

  return (
    <Dropdown as={ChatHistoryFilterDropdown} autoClose="outside">
      <Dropdown.Toggle as={ChatHistoryFilterToggle}>
        <p>{capitalizeFirstLetter(currentFilter)}</p>
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {types.map((type) => (
          <Dropdown.Item
            key={type}
            eventKey={type}
            as={ChatHistoryFilterItem}
            onClick={() => setFilter(type)}
          >
            {ChatHistoryFilterLabel[type]}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  )
}

const EditChatNameModal = (): JSX.Element => {
  const { chatName, updateChatName } = useChatNameContext()
  const [busy, setBusy] = useState(false)
  const [tempName, setTempName] = useState(chatName)
  const show = useIsShowModal(ModalFormId.RENAME_CHAT)
  const hide = useHideModal({
    modalId: ModalFormId.RENAME_CHAT,
    onHide: () => {
      setTempName(chatName)
    },
  })

  useEffect(() => {
    setTempName(chatName)
  }, [chatName])

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const trimmedName = tempName.trim()
      if (trimmedName.length && trimmedName.length <= 256 && updateChatName) {
        setBusy(true)
        await updateChatName(trimmedName)
        hide()
      }
    } catch (err) {
      log.error('Error while updating chat title:', err)
      toast.error('Error while updating chat title')
    } finally {
      setBusy(false)
    }
  }

  return (
    <ModalForm
      formId={ModalFormId.RENAME_CHAT}
      title="Rename Chat"
      show={show}
      hide={hide}
      submitText="Save"
      busy={busy}
      size="lg"
    >
      <form onSubmit={onSubmit} id={ModalFormId.RENAME_CHAT}>
        <DerivedTextInput
          autoFocus={true}
          label="Chat Name"
          name="chatName"
          initVal={tempName}
          form={ModalFormId.RENAME_CHAT}
          customOnChange={({
            currentTarget,
          }: React.ChangeEvent<HTMLInputElement>) => {
            setTempName(currentTarget?.value || '')
          }}
          isValid={!getEditChatNameError(tempName)}
          errorMessage={getEditChatNameError(tempName)}
          maxInputLength={256}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onSubmit(e)
            }
          }}
        />
      </form>
    </ModalForm>
  )
}

const chatPreviewReducer = (
  acc: Record<string, ChatPreview[]>,
  chat: ChatPreviewGRPC,
) => {
  const updatedAt = chat.getUpdatedAt()
  const dateStr = formatDate(updatedAt, false)
  if (!acc[dateStr]) {
    acc[dateStr] = []
  }
  acc[dateStr].push({
    id: chat.getId(),
    createdAt: chat.getCreatedAt(),
    updatedAt: updatedAt,
    name: chat.getName(),
    ownerId: chat.getOwner()?.getId(),
    isPrivate: chat.getIsPrivate(),
  })
  return acc
}
